import React from 'react';
import ChatWindow from './components/Chat/ChatWindow';
import { PDFViewer } from './components/PDF/PDFViewer';
import PromptLibrary from './components/Library/PromptLibrary';
import { Sidebar } from './components/Sidebar/Sidebar';
import { Config, Message, ChatHistory } from './types';
import { loadChatHistory, uploadDocument, loadConversation } from './services/api';
import './App.css';
import { useMsalAuthentication, useMsal } from '@azure/msal-react';
import { InteractionType } from '@azure/msal-browser';
import { useAuthToken } from './contexts/AuthTokenContext';
import { TopBar } from './components/TopBar/TopBar';
import { useTranslation } from 'react-i18next';
import { adminConfig, isAdminUser } from './config/adminConfig';
import { Settings, SettingsType } from './components/Settings/Settings';

// Storage key for API URL
const API_URL_STORAGE_KEY = 'businesski_api_url';

const defaultConfig: Config = {
  apiUrl: process.env.REACT_APP_API_URL || 'http://localhost:7071',
};

type View = 'chat' | 'pdf' | 'library';

function App() {
  const [currentView, setCurrentView] = React.useState('chat' as View);
  const [pdfUrl, setPdfUrl] = React.useState('');
  const [input, setInput] = React.useState('');
  const [shouldSubmit, setShouldSubmit] = React.useState(false);
  const [messages, setMessages] = React.useState<Message[]>([]);
  const [currentChatId, setCurrentChatId] = React.useState<string | null>(null);
  const [chatHistory, setChatHistory] = React.useState<ChatHistory>({ conversations: [] });
  const [showPdfViewer, setShowPdfViewer] = React.useState(false);
  const [isUploading, setIsUploading] = React.useState(false);
  const [uploadProgress, setUploadProgress] = React.useState({ current: 0, total: 0 });
  const [showSettings, setShowSettings] = React.useState(false);
  const [currentSettingsType, setCurrentSettingsType] = React.useState<SettingsType>('api');
  
  // Initialize config with saved API URL from localStorage if available
  const [config, setConfig] = React.useState<Config>(() => {
    const savedApiUrl = localStorage.getItem(API_URL_STORAGE_KEY);
    if (savedApiUrl) {
      return { ...defaultConfig, apiUrl: savedApiUrl };
    }
    return defaultConfig;
  });

  const { instance } = useMsal();
  const login = useMsalAuthentication(InteractionType.Redirect, {
    scopes: ['api://' + process.env.REACT_APP_AAD_CLIENT_ID + '/user_impersonation']
  });

  const { getToken, account } = useAuthToken();
  const userId = React.useMemo(() => account?.username || 'anonymous', [account]);
  const isAdmin = React.useMemo(() => isAdminUser(account?.username || ''), [account]);
  const canAccessSettings = React.useMemo(() => adminConfig.allowSettingsTabForUsers || isAdmin, [isAdmin]);

  const { t } = useTranslation();

  // Load chat history on mount
  React.useEffect(() => {
    const loadHistory = async () => {
      try {
        const token = await getToken();
        console.log('Loading chat history for user:', userId);
        const response = await loadChatHistory({ token, userId, apiUrl: config.apiUrl });
        console.log('Raw chat history response:', response);
        
        if (response && response.conversations) {
          console.log('Setting chat history with conversations:', response.conversations);
          setChatHistory(response);
          console.log('Chat history set successfully');
        } else {
          console.error('Invalid chat history format:', response);
          setChatHistory({ conversations: [] });
        }
      } catch (error) {
        console.error('Failed to load chat history:', error);
        setChatHistory({ conversations: [] });
      }
    };
    loadHistory();
  }, [getToken, userId, config.apiUrl]);

  const handleChatHistoryUpdate = React.useCallback(async () => {
    try {
      const token = await getToken();
      console.log('Updating chat history for user:', userId);
      const response = await loadChatHistory({ token, userId, apiUrl: config.apiUrl });
      console.log('Raw chat history update response:', response);
      
      if (response && response.conversations) {
        console.log('Updating chat history with conversations:', response.conversations);
        setChatHistory(response);
        console.log('Chat history updated successfully');
      } else {
        console.error('Invalid chat history format in update:', response);
      }
    } catch (error) {
      console.error('Failed to update chat history:', error);
    }
  }, [getToken, userId, config.apiUrl]);

  const handleUploadClick = React.useCallback(async () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.pdf,.doc,.docx,.ppt,.pptx,.xls,.xlsx';  // Allow PDF, Word, PowerPoint, and Excel files
    input.multiple = true;  // Enable multiple file selection
    
    input.onchange = async (e) => {
      const files = (e.target as HTMLInputElement).files;
      if (files && files.length > 0) {
        setIsUploading(true);
        setUploadProgress({ current: 0, total: files.length });
        
        // Track successful and failed uploads
        const successfulUploads = [];
        const failedUploads = [];
        
        // Process files sequentially
        for (let i = 0; i < files.length; i++) {
          const file = files[i];
          setUploadProgress({ current: i + 1, total: files.length });
          
          try {
            const token = await getToken();
            await uploadDocument(file, { token, userId, apiUrl: config.apiUrl });
            successfulUploads.push(file.name);
          } catch (error) {
            failedUploads.push(file.name);
          }
        }
        
        // Create status message using translation keys
        if (successfulUploads.length > 0 && failedUploads.length === 0) {
          setMessages(prev => [...prev, {
            role: 'assistant',
            content: t('chat.uploadSuccessMultiple', { count: successfulUploads.length })
          }]);
        } else if (successfulUploads.length > 0 && failedUploads.length > 0) {
          setMessages(prev => [...prev, {
            role: 'assistant',
            content: t('chat.uploadPartialSuccess', { 
              successCount: successfulUploads.length, 
              failCount: failedUploads.length 
            })
          }]);
        } else if (failedUploads.length > 0) {
          setMessages(prev => [...prev, {
            role: 'assistant',
            content: t('chat.uploadFailureMultiple', { count: failedUploads.length })
          }]);
        }
        
        setIsUploading(false);
        setUploadProgress({ current: 0, total: 0 });
      }
    };
    
    input.click();
  }, [getToken, userId, config.apiUrl, setMessages, t]);

  if (login.error) {
    console.log(login.error);
  }

  const handleOpenPdfViewer = (url: string) => {
    console.log('Opening PDF viewer with URL:', url);
    setPdfUrl(url);
    setShowPdfViewer(true);
    setCurrentView('pdf');
  };

  const handleClosePdfViewer = () => {
    console.log('Closing PDF viewer');
    setShowPdfViewer(false);
    setPdfUrl('');
    setCurrentView('chat');
  };

  const handleBackToChat = () => {
    setShowSettings(false);
    setCurrentView('chat');
  };

  const handleSelectPrompt = (prompt: string) => {
    setInput(prompt);
    setCurrentView('chat');
    setShowSettings(false);
  };

  const handleLoadConversation = async (conversationId: string) => {
    try {
      const token = await getToken();
      const conversation = await loadConversation(conversationId, { token, userId, apiUrl: config.apiUrl });
      setCurrentChatId(conversationId);
      setMessages(conversation.messages);
      setCurrentView('chat');
      setShowSettings(false);
      console.log('Loaded conversation:', conversationId);
    } catch (error) {
      console.error('Error loading conversation:', error);
    }
  };

  const handleNewChat = () => {
    setCurrentChatId(null);
    setMessages([]);
    setInput('');
    setCurrentView('chat');
    setShowSettings(false);
    console.log('Started new chat');
  };

  const handleSaveSettings = (newApiUrl: string) => {
    // Save to state
    setConfig(prev => ({ ...prev, apiUrl: newApiUrl }));
    // Save to localStorage
    localStorage.setItem(API_URL_STORAGE_KEY, newApiUrl);
    setShowSettings(false);
  };

  const handleLogout = React.useCallback(() => {
    instance.logoutRedirect({
      onRedirectNavigate: () => {
        // Reset application state
        setCurrentChatId(null);
        setMessages([]);
        setInput('');
        setCurrentView('chat');
        setChatHistory({ conversations: [] });
        return true;
      }
    });
  }, [instance]);

  const handleOpenSettings = (settingsType: SettingsType = 'api') => {
    if (canAccessSettings) {
      setShowSettings(true);
      setCurrentSettingsType(settingsType);
    }
  };

  // Reset showSettings when currentView changes
  React.useEffect(() => {
    if (currentView !== 'chat') {
      setShowSettings(false);
    }
  }, [currentView]);

  const renderMainContent = () => {
    // If settings are open, render settings view
    if (showSettings && canAccessSettings) {
      return (
        <Settings
          apiUrl={config.apiUrl}
          onApiUrlChange={(url) => setConfig(prev => ({ ...prev, apiUrl: url }))}
          onSave={() => handleSaveSettings(config.apiUrl)}
          onCancel={handleBackToChat}
          userId={userId}
          config={config}
          settingsType={currentSettingsType}
        />
      );
    }

    // Then proceed with the regular view rendering
    switch (currentView) {
      case 'chat':
        return (
          <ChatWindow
            config={config}
            input={input}
            setInput={setInput}
            shouldSubmit={shouldSubmit}
            setShouldSubmit={setShouldSubmit}
            onOpenLibrary={() => setCurrentView('library')}
            onOpenPdfViewer={handleOpenPdfViewer}
            messages={messages}
            setMessages={setMessages}
            currentChatId={currentChatId}
            setCurrentChatId={setCurrentChatId}
            showSettings={false} // Settings are now handled separately
            setShowSettings={setShowSettings}
            onSaveSettings={handleSaveSettings}
            onUpdateChatHistory={handleChatHistoryUpdate}
            userId={userId}
          />
        );
      case 'pdf':
        return (
          <div className="main-content">
            <PDFViewer
              url={pdfUrl}
              config={config}
              onClose={handleClosePdfViewer}
            />
          </div>
        );
      case 'library':
        return (
          <div className="main-content">
            <PromptLibrary
              onBack={handleBackToChat}
              onSelectPrompt={handleSelectPrompt}
              config={config}
            />
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className="app">
      <div className="chat-container">
        <Sidebar
          showPromptLibrary={currentView === 'library'}
          isUploading={isUploading}
          uploadProgress={uploadProgress}
          showPdfViewer={showPdfViewer}
          showSettings={showSettings}
          chatHistory={chatHistory.conversations}
          currentChatId={currentChatId}
          config={config}
          userId={userId}
          currentSettingsType={currentSettingsType}
          onTogglePromptLibrary={() => {
            setCurrentView(currentView === 'library' ? 'chat' : 'library');
            setShowSettings(false);
          }}
          onUploadClick={() => {
            handleUploadClick();
            setShowSettings(false);
          }}
          onTogglePdfVisibility={() => {
            handleClosePdfViewer();
            setShowSettings(false);
          }}
          onNewChat={handleNewChat}
          onLoadConversation={handleLoadConversation}
          onOpenSettings={handleOpenSettings}
          onChatHistoryUpdate={handleChatHistoryUpdate}
          onLogout={handleLogout}
          onBackToChat={handleBackToChat}
        />
        <TopBar
          userId={userId}
          onOpenSettings={() => handleOpenSettings('api')}
          onLogout={handleLogout}
        />
        <div className="main-content-wrapper">
          {renderMainContent()}
        </div>
      </div>
    </div>
  );
}

export default App;
